import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  VStack,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Portal,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { WidgetType } from '../shared/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { statisticsApi } from '@texas/api/endpoints/statisticsApi';
import { useWidgetStorage } from '../shared/useWidgetStorage';
import { ViewModeDropdown } from './ViewModeDropdown';
import {
  DateFilterDropdown,
  calculateCreatedAfterDate,
} from './DateFilterDropdown';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { Icons } from '@texas/components/shared/Icons';
import {
  SearchableMenuInput,
  SearchableMenuOption,
} from '@texas/components/shared/search/SearchableMenu';
import { SuppliersModal } from './SuppliersModal';
import { ArticlesOverviewModal } from '../shared/components/articles/ArticlesOverviewModal';
import { emptyVariantsSearchQuery } from '@texas/api/endpoints/search/searchVariantsTypes';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { createHeatmapArticleColumns } from './columns';
import { SupplierGrid } from './SupplierGrid';

interface Props extends BaseWidgetProps {
  options?: any;
}

export function SupplierCoverageHeatmapWidget({
  color,
  customTitle,
  widgetId,
}: Props) {
  const { t } = useTranslation();
  const {
    filters,
    updateMaterialId,
    updateHeatmapViewMode,
    updateArticleCreatedAfter,
  } = useWidgetStorage(widgetId);
  const [selectedCell, setSelectedCell] = useState<{
    supplierIds: number[];
    branchName: string;
    productGroupName: string;
    materialName?: string;
  } | null>(null);

  const [selectedArticleCell, setSelectedArticleCell] = useState<{
    branchId: number;
    branchName: string;
    productGroupId: number;
    productGroupName: string;
    materialId: number | null;
    materialName?: string;
  } | null>(null);

  const suppliersModalDisclosure = useDisclosure();
  const articlesModalDisclosure = useDisclosure();

  const {
    materialsData: [materials, getMaterials, loadingMaterials],
  } = useMetadata();

  const {
    data: supplierData,
    loading: loadingSuppliers,
    refetch: refetchSuppliers,
  } = useApiResource(statisticsApi.getSupplierCoverageHeatmap);

  const {
    data: articleData,
    loading: loadingArticles,
    refetch: refetchArticles,
  } = useApiResource(statisticsApi.getArticleCoverageHeatmap);

  const data =
    filters.heatmapViewMode === 'suppliers' ? supplierData : articleData;
  const loading =
    filters.heatmapViewMode === 'suppliers'
      ? loadingSuppliers
      : loadingArticles;

  useEffect(() => {
    getMaterials();
  }, [getMaterials]);

  useEffect(() => {
    if (filters.heatmapViewMode === 'suppliers') {
      refetchSuppliers({ materialId: filters.materialId ?? undefined });
    } else {
      const createdAfter = calculateCreatedAfterDate(
        filters.articleCreatedAfter,
      );
      refetchArticles({
        materialId: filters.materialId ?? undefined,
        createdAfter: createdAfter?.toISOString(),
      });
    }
  }, [
    refetchSuppliers,
    refetchArticles,
    filters.materialId,
    filters.heatmapViewMode,
    filters.articleCreatedAfter,
  ]);

  const handleViewSuppliers = useCallback(
    (data: {
      supplierIds: number[];
      branchName: string;
      productGroupName: string;
    }) => {
      const materialName = filters.materialId
        ? materials.find((m) => m.id === filters.materialId)?.name
        : undefined;

      setSelectedCell({
        ...data,
        materialName,
      });
      suppliersModalDisclosure.onOpen();
    },
    [filters.materialId, materials, suppliersModalDisclosure],
  );

  const handleViewArticles = useCallback(
    (data: {
      branchId: number;
      branchName: string;
      productGroupId: number;
      productGroupName: string;
    }) => {
      const materialName = filters.materialId
        ? materials.find((m) => m.id === filters.materialId)?.name
        : undefined;

      setSelectedArticleCell({
        ...data,
        materialId: filters.materialId,
        materialName,
      });
      articlesModalDisclosure.onOpen();
    },
    [filters.materialId, materials, articlesModalDisclosure],
  );

  const createdSinceIso = useMemo(() => {
    const createdAfter = calculateCreatedAfterDate(filters.articleCreatedAfter);
    return createdAfter?.toISOString();
  }, [filters.articleCreatedAfter]);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      customColor={color}
      type={WidgetType.SupplierCoverageHeatmap}
      style="default"
      header={customTitle ?? t('supplierCoverageHeatmap.title')}
      toolbox={
        <HStack
          spacing={2}
          overflowX="auto"
          __css={{ ' > *': { flexShrink: 0 } }}
        >
          <ViewModeDropdown
            viewMode={filters.heatmapViewMode}
            onViewModeChange={updateHeatmapViewMode}
          />
          {filters.heatmapViewMode === 'articles' && (
            <DateFilterDropdown
              selectedPreset={filters.articleCreatedAfter}
              onPresetChange={updateArticleCreatedAfter}
            />
          )}
          <HeatmapFilterHeader
            updateMaterialId={updateMaterialId}
            filters={filters}
            loadingMaterials={loadingMaterials}
            materials={materials}
          />
        </HStack>
      }
    >
      <VStack align="stretch" spacing={2} p={4}>
        <HStack spacing={2} fontSize="xs" color="gray.400">
          <Text>
            {filters.heatmapViewMode === 'suppliers'
              ? t('supplierCoverageHeatmap.lowCoverage')
              : t('supplierCoverageHeatmap.lowArticleCount')}
          </Text>
          <Box w="24px" h="24px" bg="#FEB2B2" borderRadius="2px" />
          <Box w="24px" h="24px" bg="#FC8181" borderRadius="2px" />
          <Box w="24px" h="24px" bg="#F56565" borderRadius="2px" />
          <Box w="24px" h="24px" bg="#C53030" borderRadius="2px" />
          <Text>
            {filters.heatmapViewMode === 'suppliers'
              ? t('supplierCoverageHeatmap.highCoverage')
              : t('supplierCoverageHeatmap.highArticleCount')}
          </Text>
          {loading && <SpinnerWithLabel label={t('general.loading')} />}
        </HStack>

        <Box overflowX="auto" overflowY="auto" maxH="600px">
          <SupplierGrid
            data={data}
            viewMode={filters.heatmapViewMode}
            isLoading={loading}
            handleViewSuppliers={handleViewSuppliers}
            handleViewArticles={handleViewArticles}
          />
        </Box>
      </VStack>
      {selectedCell && (
        <SuppliersModal
          isOpen={suppliersModalDisclosure.isOpen}
          onClose={suppliersModalDisclosure.onClose}
          supplierIds={selectedCell.supplierIds}
          branchName={selectedCell.branchName}
          productGroupName={selectedCell.productGroupName}
          materialName={selectedCell.materialName}
        />
      )}
      {selectedArticleCell && (
        <ArticlesOverviewModal
          isOpen={articlesModalDisclosure.isOpen}
          onClose={articlesModalDisclosure.onClose}
          filters={{
            ...emptyVariantsSearchQuery,
            branchIds: [selectedArticleCell.branchId],
            productGroupIds: [selectedArticleCell.productGroupId],
            excludeVariantsMissingPurchaseInfo: true,
            materialIds: selectedArticleCell.materialId
              ? [selectedArticleCell.materialId]
              : [],
            createdSince: createdSinceIso,
          }}
          articleSearchProps={{
            filters: [],
            searchParams: {
              ...emptyVariantsSearchQuery,
              branchIds: [selectedArticleCell.branchId],
              productGroupIds: [selectedArticleCell.productGroupId],
              excludeVariantsMissingPurchaseInfo: true,
              materialIds: selectedArticleCell.materialId
                ? [selectedArticleCell.materialId]
                : [],
              createdSince: createdSinceIso,
            },
          }}
          columns={createHeatmapArticleColumns}
        />
      )}
    </WidgetWrapper>
  );
}

interface HeatmapFilterHeaderProps {
  updateMaterialId: (id: number | null) => void;
  filters: { materialId: number | null };
  loadingMaterials: boolean;
  materials: Array<{ id: number; name: string; archived?: string }>;
}

function HeatmapFilterHeader({
  updateMaterialId,
  filters,
  loadingMaterials,
  materials,
}: HeatmapFilterHeaderProps) {
  const { t } = useTranslation();
  const [materialSearchInput, setMaterialSearchInput] = useState('');

  const materialDisplayText = useCallback(() => {
    if (filters.materialId === null)
      return t('supplierCoverageHeatmap.allMaterials');
    const material = materials.find((m) => m.id === filters.materialId);
    return material?.name ?? t('supplierCoverageHeatmap.material');
  }, [filters.materialId, materials, t]);

  const filteredMaterials = useMemo(() => {
    if (materialSearchInput.length === 0) return [];
    return materials
      .filter(
        (material) =>
          material.name
            .toLowerCase()
            .includes(materialSearchInput.toLowerCase()) && !material.archived,
      )
      .slice(0, Math.max(30, materials.length));
  }, [materials, materialSearchInput]);

  return (
    <Menu isLazy={true}>
      <MenuButton
        as={Button}
        size="sm"
        minW="fit-content"
        variant="ghost"
        rightIcon={<Icons.ChevronDown boxSize={4} />}
      >
        <Text fontSize="sm">{materialDisplayText()}</Text>
      </MenuButton>
      <Portal>
        <MenuList maxH="400px" overflowY="auto">
          <SearchableMenuInput
            placeholder={t('general.search')}
            value={materialSearchInput}
            onChange={setMaterialSearchInput}
          />
          <MenuItem
            onClick={() => {
              updateMaterialId(null);
              setMaterialSearchInput('');
            }}
            fontWeight={filters.materialId === null ? 'bold' : 'normal'}
          >
            {t('filter.all')}
          </MenuItem>
          <MenuDivider />
          {loadingMaterials ? (
            <MenuItem isDisabled={true}>{t('general.loading')}</MenuItem>
          ) : (
            <Flex flexDir="column" pb={2} gap={1} alignItems="start">
              {filteredMaterials.map((material) => (
                <SearchableMenuOption
                  key={material.id}
                  label={material.name}
                  isSelected={material.id === filters.materialId}
                  showSelectionIndicator={true}
                  onClick={() => updateMaterialId(material.id)}
                />
              ))}
            </Flex>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}
